<template>
  <main class="flex-grow container mx-auto p-4 sm:p-6">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-6">
        <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-2">Factor Practice</h1>
        <p class="text-slate-600 dark:text-slate-400 text-sm sm:text-base">
          Combine matching powers to win! Reach <span class="font-semibold text-indigo-600 dark:text-indigo-400">59,049 (3<sup>10</sup>)</span> or
          <span class="font-semibold text-emerald-600 dark:text-emerald-400">9,765,625 (5<sup>10</sup>)</span>
        </p>
      </div>

      <!-- Two Game Grids -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Powers of 3 Game -->
        <div class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-slate-200/50 dark:border-slate-700/50">
          <div class="flex justify-between items-center mb-4">
            <div>
              <h2 class="text-xl font-bold text-indigo-600 dark:text-indigo-400">Powers of 3</h2>
              <p class="text-xs text-slate-500 dark:text-slate-400">3 + 3 = 9, 9 + 9 = 27...</p>
            </div>
            <div class="flex gap-2">
              <div class="bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-1.5">
                <div class="text-xs text-slate-500 dark:text-slate-400">Score</div>
                <div class="text-lg font-bold text-slate-900 dark:text-white">{{ game3.score }}</div>
              </div>
              <button
                @click="resetGame(3)"
                class="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              >
                New
              </button>
            </div>
          </div>

          <!-- Game Board for 3s -->
          <div
            class="relative bg-indigo-100 dark:bg-indigo-900/30 rounded-xl p-2 sm:p-3 select-none touch-none"
            @touchstart="(e) => handleTouchStart(e, 3)"
            @touchend="(e) => handleTouchEnd(e, 3)"
            tabindex="0"
            @keydown="(e) => handleKeyDown(e, 3)"
            ref="gameBoard3"
          >
            <!-- Grid Background -->
            <div class="grid grid-cols-4 gap-1.5 sm:gap-2">
              <div
                v-for="i in 16"
                :key="'bg3-' + i"
                class="aspect-square bg-indigo-200/50 dark:bg-indigo-800/30 rounded-md"
              ></div>
            </div>

            <!-- Tiles -->
            <div class="absolute inset-2 sm:inset-3">
              <transition-group name="tile">
                <div
                  v-for="tile in game3.tiles"
                  :key="tile.id"
                  :class="getTileClasses(tile, 3)"
                  :style="getTileStyle(tile)"
                >
                  <span :class="getTileTextClass(tile.value)">{{ formatNumber(tile.value) }}</span>
                  <span class="text-xs opacity-75 leading-none">{{ getExponentNotation(tile.value, 3) }}</span>
                </div>
              </transition-group>
            </div>

            <!-- Win Overlay -->
            <div
              v-if="game3.won && !game3.continueAfterWin"
              class="absolute inset-0 bg-indigo-500/90 dark:bg-indigo-600/90 rounded-xl flex flex-col items-center justify-center text-white z-10"
            >
              <div class="text-3xl font-bold mb-2">You Win!</div>
              <p class="text-sm mb-4">You reached 59,049 (3<sup>10</sup>)!</p>
              <div class="flex gap-2">
                <button
                  @click="game3.continueAfterWin = true"
                  class="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  Keep Going
                </button>
                <button
                  @click="resetGame(3)"
                  class="bg-white text-indigo-600 px-4 py-2 rounded-lg text-sm font-semibold transition-colors hover:bg-indigo-50"
                >
                  New Game
                </button>
              </div>
            </div>

            <!-- Game Over Overlay -->
            <div
              v-if="game3.over && !game3.won"
              class="absolute inset-0 bg-slate-900/80 rounded-xl flex flex-col items-center justify-center text-white z-10"
            >
              <div class="text-2xl font-bold mb-2">Game Over</div>
              <p class="text-sm mb-4">Score: {{ game3.score }}</p>
              <button
                @click="resetGame(3)"
                class="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>

          <!-- Reference for 3s -->
          <div class="mt-3 text-xs text-slate-500 dark:text-slate-400 grid grid-cols-5 gap-1 text-center">
            <span>3<sup>1</sup>=3</span>
            <span>3<sup>2</sup>=9</span>
            <span>3<sup>3</sup>=27</span>
            <span>3<sup>4</sup>=81</span>
            <span>3<sup>5</sup>=243</span>
            <span>3<sup>6</sup>=729</span>
            <span>3<sup>7</sup>=2187</span>
            <span>3<sup>8</sup>=6561</span>
            <span>3<sup>9</sup>=19683</span>
            <span class="text-indigo-600 dark:text-indigo-400 font-medium">3<sup>10</sup>=59049</span>
          </div>
        </div>

        <!-- Powers of 5 Game -->
        <div class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-slate-200/50 dark:border-slate-700/50">
          <div class="flex justify-between items-center mb-4">
            <div>
              <h2 class="text-xl font-bold text-emerald-600 dark:text-emerald-400">Powers of 5</h2>
              <p class="text-xs text-slate-500 dark:text-slate-400">5 + 5 = 25, 25 + 25 = 125...</p>
            </div>
            <div class="flex gap-2">
              <div class="bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-1.5">
                <div class="text-xs text-slate-500 dark:text-slate-400">Score</div>
                <div class="text-lg font-bold text-slate-900 dark:text-white">{{ game5.score }}</div>
              </div>
              <button
                @click="resetGame(5)"
                class="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              >
                New
              </button>
            </div>
          </div>

          <!-- Game Board for 5s -->
          <div
            class="relative bg-emerald-100 dark:bg-emerald-900/30 rounded-xl p-2 sm:p-3 select-none touch-none"
            @touchstart="(e) => handleTouchStart(e, 5)"
            @touchend="(e) => handleTouchEnd(e, 5)"
            tabindex="0"
            @keydown="(e) => handleKeyDown(e, 5)"
            ref="gameBoard5"
          >
            <!-- Grid Background -->
            <div class="grid grid-cols-4 gap-1.5 sm:gap-2">
              <div
                v-for="i in 16"
                :key="'bg5-' + i"
                class="aspect-square bg-emerald-200/50 dark:bg-emerald-800/30 rounded-md"
              ></div>
            </div>

            <!-- Tiles -->
            <div class="absolute inset-2 sm:inset-3">
              <transition-group name="tile">
                <div
                  v-for="tile in game5.tiles"
                  :key="tile.id"
                  :class="getTileClasses(tile, 5)"
                  :style="getTileStyle(tile)"
                >
                  <span :class="getTileTextClass(tile.value)">{{ formatNumber(tile.value) }}</span>
                  <span class="text-xs opacity-75 leading-none">{{ getExponentNotation(tile.value, 5) }}</span>
                </div>
              </transition-group>
            </div>

            <!-- Win Overlay -->
            <div
              v-if="game5.won && !game5.continueAfterWin"
              class="absolute inset-0 bg-emerald-500/90 dark:bg-emerald-600/90 rounded-xl flex flex-col items-center justify-center text-white z-10"
            >
              <div class="text-3xl font-bold mb-2">You Win!</div>
              <p class="text-sm mb-4">You reached 9,765,625 (5<sup>10</sup>)!</p>
              <div class="flex gap-2">
                <button
                  @click="game5.continueAfterWin = true"
                  class="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  Keep Going
                </button>
                <button
                  @click="resetGame(5)"
                  class="bg-white text-emerald-600 px-4 py-2 rounded-lg text-sm font-semibold transition-colors hover:bg-emerald-50"
                >
                  New Game
                </button>
              </div>
            </div>

            <!-- Game Over Overlay -->
            <div
              v-if="game5.over && !game5.won"
              class="absolute inset-0 bg-slate-900/80 rounded-xl flex flex-col items-center justify-center text-white z-10"
            >
              <div class="text-2xl font-bold mb-2">Game Over</div>
              <p class="text-sm mb-4">Score: {{ game5.score }}</p>
              <button
                @click="resetGame(5)"
                class="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>

          <!-- Reference for 5s -->
          <div class="mt-3 text-xs text-slate-500 dark:text-slate-400 grid grid-cols-5 gap-1 text-center">
            <span>5<sup>1</sup>=5</span>
            <span>5<sup>2</sup>=25</span>
            <span>5<sup>3</sup>=125</span>
            <span>5<sup>4</sup>=625</span>
            <span>5<sup>5</sup>=3125</span>
            <span>5<sup>6</sup>=15625</span>
            <span>5<sup>7</sup>=78125</span>
            <span>5<sup>8</sup>=390625</span>
            <span>5<sup>9</sup>=1953125</span>
            <span class="text-emerald-600 dark:text-emerald-400 font-medium">5<sup>10</sup>=9765625</span>
          </div>
        </div>
      </div>

      <!-- Instructions -->
      <div class="mt-6 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-slate-200/50 dark:border-slate-700/50">
        <h3 class="font-semibold text-slate-900 dark:text-white mb-3">How to Play</h3>
        <ul class="text-sm text-slate-600 dark:text-slate-400 space-y-2">
          <li class="flex items-start gap-2">
            <span class="text-indigo-500 mt-0.5">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </span>
            <span>Use <span class="font-medium text-slate-700 dark:text-slate-300">arrow keys</span> or <span class="font-medium text-slate-700 dark:text-slate-300">swipe</span> to move tiles</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-indigo-500 mt-0.5">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </span>
            <span>Click on a game board first to control it with keyboard</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-indigo-500 mt-0.5">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </span>
            <span>Matching tiles merge: <span class="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded text-xs font-medium">3 + 3 = 9</span> and <span class="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded text-xs font-medium">5 + 5 = 25</span></span>
          </li>
        </ul>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  data() {
    return {
      game3: this.createGameState(),
      game5: this.createGameState(),
      tileIdCounter: 0,
      touchStartX: 0,
      touchStartY: 0,
      touchBase: null,
      powersOf3: [3, 9, 27, 81, 243, 729, 2187, 6561, 19683, 59049],
      powersOf5: [5, 25, 125, 625, 3125, 15625, 78125, 390625, 1953125, 9765625],
    };
  },

  methods: {
    createGameState() {
      return {
        tiles: [],
        score: 0,
        over: false,
        won: false,
        continueAfterWin: false,
      };
    },

    getGame(base) {
      return base === 3 ? this.game3 : this.game5;
    },

    getPowers(base) {
      return base === 3 ? this.powersOf3 : this.powersOf5;
    },

    getWinValue(base) {
      return base === 3 ? 59049 : 9765625;
    },

    getExponent(value, base) {
      return Math.round(Math.log(value) / Math.log(base));
    },

    formatNumber(value) {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      }
      if (value >= 10000) {
        return (value / 1000).toFixed(0) + 'K';
      }
      if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K';
      }
      return value.toString();
    },

    getTileTextClass(value) {
      if (value >= 100000) {
        return 'text-xs sm:text-sm font-bold leading-none';
      }
      if (value >= 10000) {
        return 'text-xs sm:text-base font-bold leading-none';
      }
      if (value >= 1000) {
        return 'text-sm sm:text-lg font-bold leading-none';
      }
      return 'text-base sm:text-xl font-bold leading-none';
    },

    getExponentNotation(value, base) {
      const exp = this.getExponent(value, base);
      const superscripts = { 1: '\u00B9', 2: '\u00B2', 3: '\u00B3', 4: '\u2074', 5: '\u2075', 6: '\u2076', 7: '\u2077', 8: '\u2078', 9: '\u2079', 10: '\u00B9\u2070' };
      return `${base}${superscripts[exp] || '^' + exp}`;
    },

    getNextPower(value, base) {
      const powers = this.getPowers(base);
      const idx = powers.indexOf(value);
      if (idx === -1 || idx >= powers.length - 1) return null;
      return powers[idx + 1];
    },

    getTileClasses(tile, base) {
      const exp = this.getExponent(tile.value, base);

      let colorClass = '';
      if (base === 3) {
        const colors = {
          1: 'bg-indigo-200 text-indigo-900 dark:bg-indigo-800 dark:text-indigo-100',
          2: 'bg-indigo-300 text-indigo-900 dark:bg-indigo-700 dark:text-indigo-100',
          3: 'bg-indigo-400 text-white dark:bg-indigo-600 dark:text-white',
          4: 'bg-indigo-500 text-white dark:bg-indigo-500 dark:text-white',
          5: 'bg-indigo-600 text-white dark:bg-indigo-400 dark:text-indigo-900',
          6: 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white dark:from-indigo-400 dark:to-purple-500',
          7: 'bg-gradient-to-br from-purple-600 to-pink-600 text-white',
          8: 'bg-gradient-to-br from-pink-600 to-rose-600 text-white',
          9: 'bg-gradient-to-br from-rose-600 to-orange-500 text-white',
          10: 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-600 text-white animate-pulse',
        };
        colorClass = colors[exp] || colors[1];
      } else {
        const colors = {
          1: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100',
          2: 'bg-emerald-300 text-emerald-900 dark:bg-emerald-700 dark:text-emerald-100',
          3: 'bg-emerald-400 text-white dark:bg-emerald-600 dark:text-white',
          4: 'bg-emerald-500 text-white dark:bg-emerald-500 dark:text-white',
          5: 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white dark:from-emerald-400 dark:to-teal-500',
          6: 'bg-gradient-to-br from-teal-600 to-cyan-600 text-white',
          7: 'bg-gradient-to-br from-cyan-600 to-blue-600 text-white',
          8: 'bg-gradient-to-br from-blue-600 to-violet-600 text-white',
          9: 'bg-gradient-to-br from-violet-600 to-purple-600 text-white',
          10: 'bg-gradient-to-br from-fuchsia-500 via-purple-500 to-pink-600 text-white animate-pulse',
        };
        colorClass = colors[exp] || colors[1];
      }

      return [
        'absolute flex flex-col items-center justify-center rounded-md transition-all duration-100',
        colorClass,
        tile.isNew ? 'animate-pop' : '',
        tile.isMerged ? 'animate-merge' : ''
      ];
    },

    getTileStyle(tile) {
      const cellSize = 'calc((100% - 0.5rem * 3) / 4)';
      const gapSize = '0.5rem';
      return {
        width: cellSize,
        height: cellSize,
        left: `calc(${tile.col} * (${cellSize} + ${gapSize}))`,
        top: `calc(${tile.row} * (${cellSize} + ${gapSize}))`,
      };
    },

    createTile(row, col, value) {
      return {
        id: ++this.tileIdCounter,
        row,
        col,
        value,
        isNew: true,
        isMerged: false,
      };
    },

    getEmptyCells(game) {
      const occupied = new Set(game.tiles.map(t => `${t.row},${t.col}`));
      const empty = [];
      for (let r = 0; r < 4; r++) {
        for (let c = 0; c < 4; c++) {
          if (!occupied.has(`${r},${c}`)) {
            empty.push({ row: r, col: c });
          }
        }
      }
      return empty;
    },

    spawnTile(base) {
      const game = this.getGame(base);
      const empty = this.getEmptyCells(game);
      if (empty.length === 0) return false;

      const cell = empty[Math.floor(Math.random() * empty.length)];
      game.tiles.push(this.createTile(cell.row, cell.col, base));
      return true;
    },

    resetGame(base) {
      const game = this.getGame(base);
      game.tiles = [];
      game.score = 0;
      game.over = false;
      game.won = false;
      game.continueAfterWin = false;

      this.spawnTile(base);
      this.spawnTile(base);

      setTimeout(() => {
        game.tiles.forEach(t => {
          t.isNew = false;
          t.isMerged = false;
        });
      }, 150);

      this.$nextTick(() => {
        const ref = base === 3 ? this.$refs.gameBoard3 : this.$refs.gameBoard5;
        ref?.focus();
      });
    },

    move(direction, base) {
      const game = this.getGame(base);
      if (game.over || (game.won && !game.continueAfterWin)) return false;

      game.tiles.forEach(t => {
        t.isNew = false;
        t.isMerged = false;
      });

      const vectors = {
        up: { row: -1, col: 0 },
        down: { row: 1, col: 0 },
        left: { row: 0, col: -1 },
        right: { row: 0, col: 1 },
      };

      const vector = vectors[direction];
      if (!vector) return false;

      let moved = false;
      const merged = new Set();

      const sortedTiles = [...game.tiles].sort((a, b) => {
        if (vector.row !== 0) {
          return vector.row === 1 ? b.row - a.row : a.row - b.row;
        }
        return vector.col === 1 ? b.col - a.col : a.col - b.col;
      });

      for (const tile of sortedTiles) {
        let newRow = tile.row;
        let newCol = tile.col;

        while (true) {
          const nextRow = newRow + vector.row;
          const nextCol = newCol + vector.col;

          if (nextRow < 0 || nextRow > 3 || nextCol < 0 || nextCol > 3) break;

          const blocker = game.tiles.find(t => t.row === nextRow && t.col === nextCol && t.id !== tile.id);

          if (blocker) {
            const canMerge = blocker.value === tile.value &&
                           !merged.has(blocker.id) &&
                           this.getNextPower(tile.value, base) !== null;

            if (canMerge) {
              newRow = nextRow;
              newCol = nextCol;
            }
            break;
          }

          newRow = nextRow;
          newCol = nextCol;
        }

        if (newRow !== tile.row || newCol !== tile.col) {
          moved = true;

          const mergeTarget = game.tiles.find(t => t.row === newRow && t.col === newCol && t.id !== tile.id);

          if (mergeTarget) {
            const newValue = this.getNextPower(tile.value, base);
            game.score += newValue;

            game.tiles = game.tiles.filter(t => t.id !== tile.id && t.id !== mergeTarget.id);
            const newTile = this.createTile(newRow, newCol, newValue);
            newTile.isNew = false;
            newTile.isMerged = true;
            game.tiles.push(newTile);
            merged.add(newTile.id);

            if (newValue >= this.getWinValue(base) && !game.won) {
              game.won = true;
            }
          } else {
            tile.row = newRow;
            tile.col = newCol;
          }
        }
      }

      if (moved) {
        this.$nextTick(() => {
          this.spawnTile(base);
          this.checkGameOver(base);
        });
      }

      return moved;
    },

    checkGameOver(base) {
      const game = this.getGame(base);
      if (this.getEmptyCells(game).length > 0) return;

      for (const tile of game.tiles) {
        const neighbors = [
          { row: tile.row - 1, col: tile.col },
          { row: tile.row + 1, col: tile.col },
          { row: tile.row, col: tile.col - 1 },
          { row: tile.row, col: tile.col + 1 },
        ];

        for (const n of neighbors) {
          if (n.row < 0 || n.row > 3 || n.col < 0 || n.col > 3) continue;
          const neighbor = game.tiles.find(t => t.row === n.row && t.col === n.col);
          if (neighbor && neighbor.value === tile.value && this.getNextPower(tile.value, base) !== null) {
            return;
          }
        }
      }

      game.over = true;
    },

    handleKeyDown(e, base) {
      const keyMap = {
        ArrowUp: 'up',
        ArrowDown: 'down',
        ArrowLeft: 'left',
        ArrowRight: 'right',
        w: 'up',
        s: 'down',
        a: 'left',
        d: 'right',
      };

      const direction = keyMap[e.key];
      if (direction) {
        e.preventDefault();
        this.move(direction, base);
      }
    },

    handleTouchStart(e, base) {
      const touch = e.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchBase = base;
    },

    handleTouchEnd(e, base) {
      if (this.touchBase !== base) return;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - this.touchStartX;
      const dy = touch.clientY - this.touchStartY;

      const minSwipe = 30;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      if (Math.max(absDx, absDy) < minSwipe) return;

      if (absDx > absDy) {
        this.move(dx > 0 ? 'right' : 'left', base);
      } else {
        this.move(dy > 0 ? 'down' : 'up', base);
      }
    },
  },

  mounted() {
    this.resetGame(3);
    this.resetGame(5);
  },
};
</script>

<style scoped>
.tile-enter-active,
.tile-leave-active {
  transition: all 0.1s ease;
}

.tile-enter-from {
  opacity: 0;
  transform: scale(0);
}

.tile-leave-to {
  opacity: 0;
}

@keyframes pop {
  0% { transform: scale(0); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

@keyframes merge {
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
}

.animate-pop {
  animation: pop 0.15s ease-out;
}

.animate-merge {
  animation: merge 0.15s ease-out;
}

.select-none {
  user-select: none;
  -webkit-user-select: none;
}
</style>
